'use client';

import { type FC, useCallback } from 'react';
import { Memo } from '@legendapp/state/react';
import type { SelectedGift } from '../../types/gift-selection.types';
import type { SelectedGiftsProps } from './selected-gifts.types';
// Use local GiftData instead of BaseGiftData from ui-core
import { GiftData as LocalGiftData } from '../gifts-partials/gift-data/gift-data';
import styles from './selected-gifts.module.css';
import { cn } from '@kidslabs/mario-nextjs-react-ui-core';

export const SelectedGifts: FC<SelectedGiftsProps> = ({
    FlatListComponent,
    selectedGifts$,
    onRemoveGift,
    onUpdateQuantity,
    sessionId,
    containerClassName,
}) => {
    // Memoized renderGiftItem to avoid unnecessary re-creation
    const renderSelectedGiftItem = useCallback(
        (info: { item: SelectedGift }) => {
            const { item } = info;
            const { giftData } = item;
            return (
                <Memo>
                    {() => {
                        return (
                            <div className={styles.giftItemWrapper}>
                                <LocalGiftData
                                    giftData={giftData}
                                    title={
                                        giftData?.rule?.name ??
                                        'N/A - Need to define new API to provide rule information for selected gifts'
                                    }
                                    highlighted
                                    onRemove={() => onRemoveGift(item.id)}
                                    onUpdateQuantity={quantity => onUpdateQuantity(item.id, quantity)}
                                    selectedQuantity={item.quantity}
                                    selectedGifts$={selectedGifts$} // Pass observable
                                    onSelectGift={() => {}} // No selection needed for highlighted items
                                    selectedItem={item.item}
                                    sessionId={sessionId}
                                />
                            </div>
                        );
                    }}
                </Memo>
            );
        },
        [onRemoveGift, onUpdateQuantity, selectedGifts$, sessionId]
    );

    const keyExtractor = useCallback((item: SelectedGift) => item.id, []);

    return (
        <Memo>
            {() => {
                const selectedGifts = selectedGifts$.get(); // Get value inside Memo for reactive updates

                if (!selectedGifts || selectedGifts.length === 0) {
                    return null;
                }

                // Use FlatListComponent if provided, otherwise use simple map
                if (FlatListComponent) {
                    return (
                        <FlatListComponent
                            testID="flatlist-rule-item"
                            data={selectedGifts}
                            renderItem={renderSelectedGiftItem}
                            keyExtractor={keyExtractor}
                            className={styles.list}
                        />
                    );
                }

                return (
                    <div className={cn(styles.container, containerClassName)} data-testid="flatlist-rule-item">
                        {selectedGifts.map(item => renderSelectedGiftItem({ item }))}
                    </div>
                );
            }}
        </Memo>
    );
};
